import type { Point } from '@geom';
import { mirrorStringRtl, reverseNeutralRunRtl, TextDirection } from '@strings';
import { Path } from '@svgdotjs/svg.js';
import { fixedFloat } from '@utils';
import type { Path as FKPath, Font } from 'fontkit';
import 'svg.path.js';

export interface FontPathOpts {
  weight?: number; // 100..900 typical
  italic?: boolean;
}

export function pathForStringFromFontKit(
  font: Font,
  s: string,
  position: Point,
  size: number,
  context: TextDirection,
  opts?: FontPathOpts
): Path | undefined {
  // Fix some bidi issues
  let r = mirrorStringRtl(s, context);
  r = reverseNeutralRunRtl(r, context);
  // Pick weight
  const w = opts?.weight ?? 400;
  // Get the glyphs for the string
  const glyphs = font.getVariation({ wght: w }).layout(r.trim()).glyphs;

  // Define a scale factor for font size
  const scale = size / font.unitsPerEm;

  const path = new Path();
  let isMarked = false;

  function f(x: number): number {
    return fixedFloat(x, 3);
  }

  // Start the pen position at the specified position
  let px = position.x;
  const py = position.y;

  const shear = opts?.italic ? Math.tan((12 * Math.PI) / 180) : 0; // ~12deg italic shear

  function tx(x: number, y: number): number {
    // apply shear in X by Y amount
    return px + x * scale + shear * (y * scale);
  }
  function ty(y: number): number {
    return py - y * scale;
  }

  for (const glyph of glyphs) {
    // Get the path data for the glyph
    const glyphPath: FKPath = glyph.path;

    // Translate and scale the path data
    for (const cmd of glyphPath.commands) {
      switch (cmd.command) {
        case 'moveTo':
          {
            const [x, y] = cmd.args;
            path.M({ x: f(tx(x, y)), y: f(ty(y)) });
          }
          break;
        case 'lineTo':
          {
            const [x, y] = cmd.args;
            path.L({ x: f(tx(x, y)), y: f(ty(y)) });
            isMarked = true;
          }
          break;
        case 'bezierCurveTo':
          {
            const [x1, y1, x2, y2, x, y] = cmd.args;
            path.C(
              { x: f(tx(x1, y1)), y: f(ty(y1)) },
              { x: f(tx(x2, y2)), y: f(ty(y2)) },
              { x: f(tx(x, y)), y: f(ty(y)) }
            );
            isMarked = true;
          }
          break;
        case 'quadraticCurveTo':
          {
            const [x1, y1, x, y] = cmd.args;
            path.Q({ x: f(tx(x1, y1)), y: f(ty(y1)) }, { x: f(tx(x, y)), y: f(ty(y)) });
            isMarked = true;
          }
          break;
        case 'closePath':
          path.Z();
          break;
      }
    }

    // Move the pen position to the right
    px += glyph.advanceWidth * scale;
  }

  if (isMarked) {
    return path;
  } else {
    // Can happen if the string is empty, or if it only contains whitespace, or
    // if it is an emoji or other unsupported character.
    return undefined;
  }
}
